import { queriesWorkLogs } from "@query";
import { WorkLog } from "@wingspanhq/wingspan-api-sdk";

import { selectorPayStatementWorkLogIds } from "../../PayrollRuns/selectors/selectorPayStatementWorkLogIds";
import { useQueryWorkLogConvertedResource } from "./useQueryWorkLogConvertedResource";

/**
 * The *other* work logs that cancelling this one's pay statement would release.
 *
 * One pay statement can pay several work logs — a contractor working multiple
 * sites files one per engagement — and cancelling it deletes the statement and
 * sends every one of them back to awaiting approval. The payer has to see which,
 * because each then needs approving again separately.
 *
 * The statement carries no `sourceWorkLogIds`; each earning line names the work
 * log it paid, so that is the source set. `listWorkLogs` takes no id filter, so
 * the period's work logs are fetched and narrowed to that set client-side —
 * scoped to the statement's own payee, since a statement covers one person and
 * the unscoped read would pull every work log the payer has for the period.
 *
 * Empty for a contractor work log: a payable pays exactly one.
 */
export const useQueryWorkLogsReleasedByCancel = (
  workLog?: WorkLog
): { workLogs: WorkLog[]; isLoading: boolean } => {
  const { payStatement, queryPayStatement } =
    useQueryWorkLogConvertedResource(workLog);

  const payPeriodId = payStatement?.payPeriodId ?? undefined;
  const payeeAccountId = payStatement?.payeeAccountId ?? undefined;

  const queryWorkLogs = queriesWorkLogs.listWorkLogs.useQueryAll(
    {
      filter: {
        payPeriodId: { eq: payPeriodId as string },
        payeeAccountId: { eq: payeeAccountId as string }
      }
    },
    { enabled: !!payPeriodId && !!payeeAccountId }
  );

  const paidWorkLogIds = payStatement
    ? new Set(selectorPayStatementWorkLogIds(payStatement))
    : undefined;

  const workLogs = paidWorkLogIds
    ? (queryWorkLogs.flatData ?? []).filter(
        sibling =>
          sibling.id &&
          sibling.id !== workLog?.id &&
          paidWorkLogIds.has(sibling.id)
      )
    : [];

  return {
    workLogs,
    isLoading:
      queryPayStatement.isInitialLoading || queryWorkLogs.isInitialLoading
  };
};
